import API from "API";
import {
    DeleteOutlined,
    InboxOutlined,
    ReloadOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Popconfirm,
    Space,
    Table,
    Tag,
    Typography,
    Upload,
    message,
} from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";

const { Dragger } = Upload;
const { Title, Text } = Typography;

const statusColors = {
    pending: "default",
    processing: "processing",
    ready: "success",
    failed: "error",
};

const statusLabels = {
    pending: "Pending",
    processing: "Processing",
    ready: "Ready",
    failed: "Failed",
};

function formatDate(value) {
    if (!value) return "-";
    return new Date(value).toLocaleString("vi-VN");
}

export default function AdminDocuments() {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [docs, setDocs] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();

    const loadDocuments = useCallback(() => {
        setLoading(true);
        API.get("rag/documents/")
            .then((res) => {
                setDocs(res.data || []);
            })
            .catch(() => {
                messageApi.error("Cannot load documents.");
            })
            .finally(() => setLoading(false));
    }, [messageApi]);

    useEffect(() => {
        document.title = "hoctot.ai - Quản lý tài liệu chatbot";
        loadDocuments();
    }, [loadDocuments]);

    const handleDelete = (id) => {
        API.delete(`rag/documents/${id}/`)
            .then(() => {
                messageApi.success("Document deleted.");
                loadDocuments();
            })
            .catch(() => {
                messageApi.error("Delete failed.");
            });
    };

    const handleReindex = (id) => {
        API.post(`rag/documents/${id}/reindex/`)
            .then(() => {
                messageApi.success("Re-embedding completed.");
                loadDocuments();
            })
            .catch((err) => {
                const detail = err?.response?.data?.error_message || err?.response?.data?.detail;
                messageApi.error(detail || "Re-embedding failed.");
                loadDocuments();
            });
    };

    const uploadProps = {
        multiple: false,
        maxCount: 1,
        accept: ".txt,.pdf,.docx",
        customRequest: ({ file, onError, onSuccess }) => {
            setUploading(true);
            const formData = new FormData();
            formData.append("file", file);
            formData.append("title", file.name);
            API.post("rag/documents/", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
                .then(() => {
                    onSuccess("ok");
                    messageApi.success("Upload completed.");
                    loadDocuments();
                })
                .catch((err) => {
                    const detail = err?.response?.data?.detail;
                    messageApi.error(detail || "Upload failed.");
                    onError(err);
                })
                .finally(() => setUploading(false));
        },
    };

    const columns = useMemo(
        () => [
            {
                title: "Tên tài liệu",
                dataIndex: "title",
                key: "title",
                render: (_, row) => (
                    <Space direction="vertical" size={0}>
                        <Text strong>{row.title || row.file?.split("/").pop()}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {row.file}
                        </Text>
                    </Space>
                ),
            },
            {
                title: "Trạng thái",
                dataIndex: "status",
                key: "status",
                width: 140,
                render: (value) => (
                    <Tag color={statusColors[value] || "default"}>
                        {statusLabels[value] || value}
                    </Tag>
                ),
            },
            {
                title: "Chunks",
                dataIndex: "chunk_count",
                key: "chunk_count",
                width: 100,
                align: "right",
            },
            {
                title: "Thời gian tải",
                dataIndex: "created_at",
                key: "created_at",
                width: 200,
                render: formatDate,
            },
            {
                title: "Lỗi",
                dataIndex: "error_message",
                key: "error_message",
                width: 240,
                render: (value) => (
                    <Text type="danger" style={{ fontSize: 12 }}>
                        {value || "-"}
                    </Text>
                ),
            },
            {
                title: "Hành động",
                key: "actions",
                width: 220,
                render: (_, row) => (
                    <Space>
                        {(row.status === "failed" || row.status === "pending") && (
                            <Button
                                icon={<SyncOutlined />}
                                loading={row.status === "processing"}
                                onClick={() => handleReindex(row.id)}
                            >
                                Embed lại
                            </Button>
                        )}
                        <Popconfirm
                            title="Delete this document?"
                            onConfirm={() => handleDelete(row.id)}
                            okText="Delete"
                            cancelText="Cancel"
                        >
                            <Button danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [loadDocuments, messageApi]
    );

    return (
        <>
            {contextHolder}
            <Card>
                <Space
                    style={{ width: "100%", justifyContent: "space-between", marginBottom: 16 }}
                    align="start"
                >
                    <div>
                        <Title level={4} style={{ marginBottom: 8 }}>
                            Quản lý tài liệu cho chatbot
                        </Title>
                        <Text type="secondary">
                            Upload tài liệu để hệ thống RAG index và trả lời câu hỏi.
                        </Text>
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={loadDocuments} loading={loading}>
                        Refresh
                    </Button>
                </Space>

                <Dragger {...uploadProps} disabled={uploading} style={{ marginBottom: 20 }}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click hoặc kéo thả tài liệu vào đây để upload</p>
                    <p className="ant-upload-hint">Hỗ trợ .txt, .pdf, .docx</p>
                </Dragger>

                <Table
                    rowKey="id"
                    columns={columns}
                    dataSource={docs}
                    loading={loading}
                    pagination={{ pageSize: 8 }}
                    scroll={{ x: 1000 }}
                />
            </Card>
        </>
    );
}
